import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiShoppingCart, FiUser, FiChevronLeft, FiChevronRight, FiLogOut, FiGlobe, FiChevronDown } from 'react-icons/fi';
import logo from '../logo.svg';
import { useCategories } from '../hooks/useCategories';
import { useAllSubcategories } from '../hooks/useSubcategories';
import { useAuthContext } from '../hooks/useAuthContext';
import { useCart } from '../hooks/useCart';
import { useLanguage } from '../context/LanguageContext';
import { slugify } from '../utils/slugify';
import { supabase } from '../config/supabase';

const logoArabic = '/logo-arabic.svg';

const HeaderContainer = styled.header`
  background: #5efba9;
  border-bottom: 1px solid #e5e7eb;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
`;

const TopBar = styled.div`
  background: #5efba9;
  padding: 4px 0;
  display: none;
`;

const TopBarContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 14px;
  color: white;
`;

const MainHeader = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: #5efba9;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  
  img {
    width: 140px;
    height: auto;
    max-height: 60px;
    object-fit: contain;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const SellButton = styled(Link)`
  padding: 10px 20px;
  background: #1f2937;
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s ease;
  white-space: nowrap;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    background: #111827;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const LanguageButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #1f2937;
  border: 2px solid #e5e7eb;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background: #1f2937;
    transition: width 0.3s ease;
    z-index: 0;
  }

  &:hover {
    border-color: #1f2937;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    
    &:before {
      width: 100%;
    }
    
    span, svg {
      position: relative;
      z-index: 1;
      color: white;
    }
  }

  svg {
    font-size: 16px;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: rotate(180deg);
  }

  span, svg {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
    gap: 6px;
  }
`;

const IconGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  .desktop-only {
    display: inline-flex;
  }

  .mobile-only {
    display: none;
  }

  @media (max-width: 768px) {
    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: flex;
    }
  }
`;

const IconButton = styled(Link)`
  position: relative;
  color: #1f2937;
  font-size: 22px;
  text-decoration: none;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #5efba9;
  }
`;


const Badge = styled.small`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #1f2937;
  color: white;
  border-radius: 50%;
  min-width: 16px;
  height: 16px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 2px;
`;

const ProfileDropdownContainer = styled.div`
  position: relative;
  z-index: 1002;
`;

const ProfileIconButton = styled.button`
  position: relative;
  color: #1f2937;
  font-size: 22px;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    color: #111827;
    transform: scale(1.1);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  ${props => {
    // For RTL (Arabic), always align to left side of button
    if (props.$isRTL) {
      return `
        right: auto;
        left: 0;
      `;
    }
    // For LTR (English), align to right side of button
    return `
      right: 0;
      left: auto;
    `;
  }}
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-width: calc(100vw - 40px);
  z-index: 1002;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  pointer-events: ${props => (props.$isOpen ? 'auto' : 'none')};
  transform: ${props => (props.$isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  direction: ${props => (props.$isRTL ? 'rtl' : 'ltr')};

  @media (max-width: 768px) {
    right: ${props => (props.$isRTL ? 'auto' : '0')};
    left: ${props => (props.$isRTL ? '0' : 'auto')};
    max-width: calc(100vw - 20px);
  }

  &:before {
    content: '';
    position: absolute;
    top: -6px;
    ${props => (props.$isRTL ? 'left: 20px;' : 'right: 20px;')}
    width: 12px;
    height: 12px;
    background: white;
    transform: rotate(45deg);
    box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #1f2937;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  border-bottom: 1px solid #f3f4f6;
  direction: ${props => (props.$isRTL ? 'rtl' : 'ltr')};
  flex-direction: ${props => (props.$isRTL ? 'row-reverse' : 'row')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
    color: #5efba9;
  }

  svg {
    font-size: 18px;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  width: 100%;
  background: none;
  border: none;
  color: #1f2937;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  text-align: ${props => (props.$isRTL ? 'right' : 'left')};
  direction: ${props => (props.$isRTL ? 'rtl' : 'ltr')};

  &:hover {
    background: #fef2f2;
    color: #dc2626;
  }

  svg {
    font-size: 18px;
  }
`;

const LanguageCurrency = styled.select`
  padding: 2px 6px;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 13px;
  cursor: pointer;
  
  option {
    background: #5efba9;
    color: white;
  }
`;

const CategoryBar = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-top: 2px solid #e5e7eb;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.03);
  position: relative;
  z-index: 1001;
  overflow: visible;
`;

const CategoryContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 8px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1001;
  overflow: visible;
`;


const CategoryMenu = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  overflow-y: visible;
  flex: 1;
  padding-bottom: 8px;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const CategoryItem = styled.div`
  position: relative;
  display: inline-block;
  z-index: ${props => (props.$isHovered ? 1002 : 'auto')};
`;

const CategoryLink = styled(Link)`
  padding: 6px 16px;
  border: none;
  border-radius: 8px;
  background: ${props => (props.$active ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' : 'white')};
  color: ${props => (props.$active ? '#065f46' : '#475569')};
  font-size: 13px;
  font-weight: ${props => (props.$active ? '700' : '600')};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${props => (props.$active ? '#5efba9' : 'transparent')};
  box-shadow: ${props => (props.$active ? '0 2px 8px rgba(94, 251, 169, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)')};
  position: relative;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${props => props.$active
    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
    : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$active
    ? '0 4px 12px rgba(94, 251, 169, 0.3)'
    : '0 4px 12px rgba(0, 0, 0, 0.1)'};
    border-color: ${props => (props.$active ? '#5efba9' : '#cbd5e1')};
  }
`;

const SubcategoryDropdown = styled.div`
  position: fixed;
  top: ${props => props.top || 'auto'}px;
  left: ${props => props.left || 'auto'}px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-width: 600px;
  z-index: 9999 !important;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  padding: 12px 0;
  border: 1px solid #e5e7eb;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  
  /* Grid layout: exactly 3 items per column */
  display: ${props => props.isVisible ? 'grid' : 'none'};
  grid-template-rows: repeat(3, auto);
  grid-auto-flow: column;
  grid-auto-columns: 180px;
  gap: 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SubcategoryLink = styled(Link)`
  display: block;
  padding: 10px 20px;
  color: #1f2937;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 0;
  box-sizing: border-box;

  &:hover {
    background: #f0fdf4;
    color: #5efba9;
    padding-left: 24px;
  }
`;

const ChevronIcon = styled(FiChevronDown)`
  font-size: 12px;
  transition: transform 0.3s;
  transform: ${props => (props.$isHovered ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const ScrollButton = styled.button`
  background: white;
  border: 2px solid #e5e7eb;
  color: #64748b;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  &:hover:not(:disabled) {
    color: #5efba9;
    border-color: #5efba9;
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 4px 12px rgba(94, 251, 169, 0.3);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    background: #f1f5f9;
    border-color: #e2e8f0;
  }
`;

function Header() {
  const { categories } = useCategories();
  const { subcategoriesByCategory, loading: subcategoriesLoading } = useAllSubcategories();
  const { isLoggedIn } = useAuthContext();
  const { cartCount } = useCart();
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const categoryMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredCategoryId, setHoveredCategoryId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const categoryLinkRefs = useRef({});

  // Update dropdown position when hovering or scrolling
  useEffect(() => {
    if (hoveredCategoryId) {
      const linkElement = categoryLinkRefs.current[hoveredCategoryId];
      if (linkElement) {
        const updatePosition = () => {
          const rect = linkElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left
          });
        };
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        return () => window.removeEventListener('scroll', updatePosition, true);
      }
    }
  }, [hoveredCategoryId]);


  // --- added: local header badge state + listeners for immediate updates ---
  const [headerCartCount, setHeaderCartCount] = useState(cartCount || 0);

  // keep local copy in sync when hook updates
  useEffect(() => {
    setHeaderCartCount(cartCount || 0);
  }, [cartCount]);

  // listen for 'cartUpdated' CustomEvent dispatched by useCart
  useEffect(() => {
    const onCartUpdated = (e) => {
      const newCount = e?.detail?.count
        ?? (Array.isArray(e?.detail?.items) ? e.detail.items.reduce((s, i) => s + (i.quantity || 0), 0) : 0);
      setHeaderCartCount(newCount);
    };

    window.addEventListener('cartUpdated', onCartUpdated);
    return () => window.removeEventListener('cartUpdated', onCartUpdated);
  }, []);

  const displayCategories = categories.map(cat => ({
    id: cat.id,
    name: language === 'ar' ? (cat.name_ar || cat.name_en || cat.name) : (cat.name_en || cat.name),
    slug: slugify(cat.name_en || cat.name),
    img: cat.img
  }));

  // Get current category from URL
  const currentPath = location.pathname;
  const isOnCategoriesPage = currentPath === '/categories' || currentPath.startsWith('/category/');
  const currentCategorySlug = currentPath.includes('/category/')
    ? currentPath.split('/category/')[1]?.split('/')[0] // Get first part (category slug, ignoring subcategory)
    : null;

  // Check scroll position
  const checkScrollButtons = () => {
    if (categoryMenuRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryMenuRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const menu = categoryMenuRef.current;
    if (menu) {
      menu.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        menu.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [categories]);

  // Handle click outside dropdown and adjust position
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    // Adjust dropdown position to prevent going off-screen (simplified for RTL)
    const adjustDropdownPosition = () => {
      if (dropdownRef.current && isDropdownOpen) {
        const button = dropdownRef.current.querySelector('button');
        const dropdown = dropdownRef.current.querySelector('[data-dropdown-menu]');
        if (button && dropdown) {
          const buttonRect = button.getBoundingClientRect();
          const dropdownWidth = 200; // min-width of dropdown
          const viewportWidth = window.innerWidth;
          
          // For RTL (Arabic), check if dropdown fits on left side
          if (language === 'ar') {
            const spaceOnLeft = buttonRect.left;
            // If not enough space on left, we might need to adjust, but default to left
            if (spaceOnLeft < dropdownWidth) {
              // Could adjust here if needed, but for now keep left alignment
            }
          } else {
            // For LTR, check if dropdown fits on right side
            const spaceOnRight = viewportWidth - buttonRect.right;
            if (spaceOnRight < dropdownWidth) {
              // Adjust dropdown position if needed
            } else {
              // Dropdown fits on right side
            }
          }
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Adjust position after a small delay to ensure DOM is updated
      setTimeout(adjustDropdownPosition, 0);
      window.addEventListener('resize', adjustDropdownPosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', adjustDropdownPosition);
      };
    }
  }, [isDropdownOpen, language]);

  const scrollLeft = () => {
    if (categoryMenuRef.current) {
      categoryMenuRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (categoryMenuRef.current) {
      categoryMenuRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <HeaderContainer>
      <TopBar>
        <TopBarContent>
          <div style={{ display: 'flex', gap: '12px' }}>
            <LanguageCurrency>
              <option value="en">English</option>
              <option value="ar">AR</option>
            </LanguageCurrency>
            <LanguageCurrency>
              <option value="omr">OMR</option>
            </LanguageCurrency>
          </div>
        </TopBarContent>
      </TopBar>
      <MainHeader>
        <Logo to="/">
          <img src={language === 'ar' ? logoArabic : logo} alt="WASL Logo" />
        </Logo>

        <RightSection>
          <SellButton to="/sell-with-us">{language === 'ar' ? 'البيع معنا' : 'Sell With Us'}</SellButton>
          <LanguageButton onClick={toggleLanguage} title="Switch Language">
            <FiGlobe />
            <span>{language === 'en' ? 'AR' : 'EN'}</span>
          </LanguageButton>
          <IconGroup>
            <IconButton to="/cart">
              <FiShoppingCart />
              <Badge>{headerCartCount || 0}</Badge>
            </IconButton>
            {isLoggedIn ? (
              <ProfileDropdownContainer ref={dropdownRef}>
                <ProfileIconButton
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  title="Profile Menu"
                >
                  <FiUser />
                </ProfileIconButton>

                <DropdownMenu $isOpen={isDropdownOpen} $isRTL={language === 'ar'} data-dropdown-menu>
                  <DropdownItem to="/profile" onClick={() => setIsDropdownOpen(false)} $isRTL={language === 'ar'}>
                    <FiUser />
                    {language === 'ar' ? 'الملف الشخصي' : 'Profile'}
                  </DropdownItem>
                  <DropdownButton
                    type="button"
                    onClick={async () => {
                      setIsDropdownOpen(false);
                      try {
                        // Always clear local session first (reliable)
                        const { error: localError } = await supabase.auth.signOut();
                        if (localError) console.error('Sign out error (local):', localError);

                        // Best-effort revoke (non-blocking)
                        supabase.auth.signOut({ scope: 'global' }).catch(() => {});
                      } catch (e) {
                        console.error('Sign out failed:', e);
                      }
                      window.location.assign('/');
                    }}
                    $isRTL={language === 'ar'}
                  >
                    <FiLogOut />
                    {language === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}
                  </DropdownButton>
                </DropdownMenu>
              </ProfileDropdownContainer>
            ) : (
              <IconButton to="/login">
                <FiUser />
              </IconButton>
            )}
          </IconGroup>
        </RightSection>
      </MainHeader>

      <CategoryBar>
        <CategoryContent>
          <CategoryMenu ref={categoryMenuRef}>
            <ScrollButton onClick={scrollLeft} disabled={!canScrollLeft} title="Scroll left">
              {language === 'ar' ? <FiChevronRight /> : <FiChevronLeft />}
            </ScrollButton>
            <CategoryLink
              to="/categories"
              $active={isOnCategoriesPage && !currentCategorySlug}
            >
              {language === 'ar' ? 'الكل' : 'All'}
            </CategoryLink>
            {displayCategories.map((category, index) => {
              // Match category ID - try both direct match and string conversion
              const categoryId = category.id;
              const categoryIdStr = String(categoryId);
              
              // Try to find subcategories by matching the category ID
              let subcategories = [];
              if (subcategoriesByCategory[categoryId]) {
                subcategories = subcategoriesByCategory[categoryId];
              } else if (subcategoriesByCategory[categoryIdStr]) {
                subcategories = subcategoriesByCategory[categoryIdStr];
              } else {
                // Try to find by matching any key that equals the category ID
                const matchingKey = Object.keys(subcategoriesByCategory).find(
                  key => String(key) === String(categoryId) || key === categoryId
                );
                if (matchingKey) {
                  subcategories = subcategoriesByCategory[matchingKey];
                }
              }
              
              const hasSubcategories = subcategories && subcategories.length > 0;
              const isHovered = hoveredCategoryId === category.id;
              
              // Debug for first few categories
              if (index < 3) {
                console.log(`Category ${index}:`, {
                  categoryId: category.id,
                  categoryIdType: typeof category.id,
                  categoryIdStr,
                  subcategoriesByCategoryKeys: Object.keys(subcategoriesByCategory).slice(0, 3),
                  foundSubcategories: subcategories.length,
                  hasSubcategories
                });
              }
              
              return (
                <CategoryItem
                  key={index}
                  $isHovered={isHovered}
                  data-category-item={category.id}
                  onMouseEnter={(e) => {
                    if (hasSubcategories) {
                      const linkElement = e.currentTarget.querySelector('a');
                      if (linkElement) {
                        const rect = linkElement.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + 4,
                          left: rect.left
                        });
                      }
                      setHoveredCategoryId(category.id);
                    }
                  }}
                  onMouseLeave={(e) => {
                    // Only close if not moving to dropdown
                    const relatedTarget = e.relatedTarget;
                    if (!relatedTarget || typeof relatedTarget.closest !== 'function' || !relatedTarget.closest(`[data-subcategory-dropdown="${category.id}"]`)) {
                      // Add a small delay to allow moving to dropdown
                      setTimeout(() => {
                        const dropdown = document.querySelector(`[data-subcategory-dropdown="${category.id}"]`);
                        if (!dropdown || !dropdown.matches(':hover')) {
                          setHoveredCategoryId(null);
                        }
                      }, 150);
                    }
                  }}
                >
                  <CategoryLink
                    ref={el => categoryLinkRefs.current[category.id] = el}
                    to={`/category/${category.slug}`}
                    $active={currentCategorySlug === category.slug}
                  >
                    {category.name}
                    {hasSubcategories && (
                      <ChevronIcon $isHovered={isHovered} />
                    )}
                  </CategoryLink>
                  {hasSubcategories && isHovered && (
                    <SubcategoryDropdown 
                      data-subcategory-dropdown={category.id}
                      isVisible={true}
                      top={dropdownPosition.top}
                      left={dropdownPosition.left}
                      onMouseEnter={() => {
                        setHoveredCategoryId(category.id);
                      }}
                      onMouseLeave={(e) => {
                        // Only close if not moving back to category
                        const relatedTarget = e.relatedTarget;
                        if (!relatedTarget || typeof relatedTarget.closest !== 'function' || !relatedTarget.closest(`[data-category-item="${category.id}"]`)) {
                          setTimeout(() => {
                            const categoryItem = document.querySelector(`[data-category-item="${category.id}"]`);
                            if (!categoryItem || !categoryItem.matches(':hover')) {
                              setHoveredCategoryId(null);
                            }
                          }, 100);
                        }
                      }}
                    >
                      {subcategories.map((subcat) => {
                        const subcatSlug = subcat.slug || slugify(subcat.name_en || subcat.name_ar || '');
                        const subcatName = language === 'ar' 
                          ? (subcat.name_ar || subcat.name_en || '') 
                          : (subcat.name_en || '');
                        return (
                          <SubcategoryLink
                            key={subcat.id}
                            to={`/category/${category.slug}/${subcatSlug}`}
                            onClick={() => setHoveredCategoryId(null)}
                          >
                            {subcatName}
                          </SubcategoryLink>
                        );
                      })}
                    </SubcategoryDropdown>
                  )}
                </CategoryItem>
              );
            })}
            <ScrollButton onClick={scrollRight} disabled={!canScrollRight} title="Scroll right">
              {language === 'ar' ? <FiChevronLeft /> : <FiChevronRight />}
            </ScrollButton>
          </CategoryMenu>
        </CategoryContent>
      </CategoryBar>
    </HeaderContainer>
  );
}

export default Header;
